import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus, Edit2, Trash2, Search, Check, X,
  Upload, FileText, Bold, Italic, List, ListOrdered, Undo, Redo
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import StatusBadge from './StatusBadge';
import WorkflowActions from './WorkflowActions';
import FileUploadInput, { FILE_ACCEPT_TYPES } from './FileUploadInput';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Rich Text Editor Component using TipTap
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none min-h-[120px] p-3 focus:outline-none',
      },
    },
  });

  // Update editor content when value changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  if (!editor) {
    return (
      <div className="border border-gray-300 rounded-lg p-3 min-h-[150px] bg-gray-50 animate-pulse" />
    );
  }

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#0E6D2B] focus-within:border-transparent">
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-200">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${editor.isActive('bold') ? 'bg-gray-200 text-[#0E6D2B]' : 'text-gray-600'}`}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${editor.isActive('italic') ? 'bg-gray-200 text-[#0E6D2B]' : 'text-gray-600'}`}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${editor.isActive('bulletList') ? 'bg-gray-200 text-[#0E6D2B]' : 'text-gray-600'}`}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${editor.isActive('orderedList') ? 'bg-gray-200 text-[#0E6D2B]' : 'text-gray-600'}`}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className="p-1.5 rounded hover:bg-gray-200 transition-colors text-gray-600 disabled:opacity-40"
          title="Undo"
        >
          <Undo className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className="p-1.5 rounded hover:bg-gray-200 transition-colors text-gray-600 disabled:opacity-40"
          title="Redo"
        >
          <Redo className="w-4 h-4" />
        </button>
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} />
    </div>
  );
};

export default function ContentManager({
  title,
  subtitle,
  contentType,
  fields,
  columns
}) {
  const { isSuperAdmin, isCreator, isAdmin, user } = useAuth();  // Get user info
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [fileData, setFileData] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // Progress State
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [activeFieldGroup, setActiveFieldGroup] = useState('basic');
  const [selectedItemForWorkflow, setSelectedItemForWorkflow] = useState(null);

  // Group fields by category for better organization
  const groupedFields = useCallback(() => {
    const groups = {
      basic: { label: 'Basic Info', fields: [] },
      details: { label: 'Details', fields: [] },
      dates: { label: 'Dates', fields: [] },
      files: { label: 'Files & Media', fields: [] },
      settings: { label: 'Settings', fields: [] }
    };

    fields.forEach(field => {
      if (field.type === 'file') {
        groups.files.fields.push(field);
      } else if (field.type === 'date') {
        groups.dates.fields.push(field);
      } else if (field.type === 'checkbox') {
        groups.settings.fields.push(field);
      } else if (field.type === 'richtext' || field.type === 'textarea') {
        groups.details.fields.push(field);
      } else {
        groups.basic.fields.push(field);
      }
    });

    // Filter out empty groups
    return Object.entries(groups).filter(([, group]) => group.fields.length > 0);
  }, [fields]);

  // Map content types to correct API paths
  const getApiPath = useCallback((type) => {
    const pathMap = {
      'news_events': 'news_events',
      'useful_links': 'useful_links',
      'social_feeds': 'social_feeds',
    };
    return pathMap[type] || type;
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      const token = localStorage.getItem('cish_token');
      const apiPath = getApiPath(contentType);
      const response = await axios.get(`${API_URL}/api/${apiPath}?published_only=false`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [contentType, getApiPath]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openCreateDialog = () => {
    const initialData = {};
    fields.forEach(field => {
      if (field.defaultValue !== undefined) {
        initialData[field.name] = field.defaultValue;
      } else if (field.type === 'checkbox') {
        initialData[field.name] = false;
      } else if (field.type === 'number') {
        initialData[field.name] = field.defaultValue || 0;
      } else {
        initialData[field.name] = '';
      }
    });
    setFormData(initialData);
    setFileData({});
    setEditItem(null);
    setUploadProgress(0);
    const groups = groupedFields();
    setActiveFieldGroup(groups[0]?.[0] || 'basic');
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setFormData({ ...item });
    setFileData({});
    setEditItem(item);
    setUploadProgress(0);
    const groups = groupedFields();
    setActiveFieldGroup(groups[0]?.[0] || 'basic');
    setDialogOpen(true);
  };

  const deleteFileFromServer = async (url) => {
    if (!url) return false;

    // Attempt to extract file ID from URL. 
    // Expected formats: /api/files/{id}, /api/files/{id}/download, or just {id}
    let fileId = null;
    if (url.includes('/api/files/')) {
      const parts = url.split('/api/files/');
      if (parts.length > 1) {
        fileId = parts[1].split('/')[0];
      }
    }

    if (fileId) {
      if (!window.confirm("WARNING: This will permanently delete this file from the server. This action cannot be undone. Continue?")) {
        return false;
      }

      try {
        const token = localStorage.getItem('cish_token');
        await axios.delete(`${API_URL}/api/files/${fileId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success("File permanently deleted from server");
        return true;
      } catch (e) {
        console.error(e);
        toast.error("Failed to delete file: " + (e.response?.data?.detail || e.message));
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    setSaving(true);
    setUploadProgress(0);
    try {
      const token = localStorage.getItem('cish_token');
      const apiPath = getApiPath(contentType);
      const method = editItem ? 'PUT' : 'POST';

      // For creators: enforce workflow rules
      if (isCreator) {
        formData.is_published = false;
        formData.visible_to_public = false;
        formData.workflow_status = 'PENDING';
      }

      // For admins/superadmins: auto-publish
      if (isAdmin || isSuperAdmin) {
        formData.workflow_status = 'PUBLISHED';
        if (formData.is_published !== false) {
          formData.is_published = true;
        }
        if (formData.visible_to_public !== false) {
          formData.visible_to_public = true;
        }
      }

      // Check if we have any file uploads (including arrays/FileLists for multiple files)
      const hasFiles = Object.keys(fileData).some(key => {
        const val = fileData[key];
        if (val instanceof File) return true;
        if (val instanceof FileList && val.length > 0) return true;
        if (Array.isArray(val) && val.length > 0 && val[0] instanceof File) return true;
        return false;
      });

      let response;

      if (hasFiles) {
        // Use upload endpoints for file submissions
        const uploadPath = `${apiPath}-upload`;
        const url = editItem
          ? `${API_URL}/api/${uploadPath}/${editItem.id}`
          : `${API_URL}/api/${uploadPath}`;

        // Use FormData for multipart upload
        const formDataObj = new FormData();

        // Add all regular form fields
        Object.keys(formData).forEach(key => {
          const value = formData[key];
          if (value !== null && value !== undefined && value !== '') {
            if (typeof value === 'boolean') {
              formDataObj.append(key, value.toString());
            } else if (Array.isArray(value)) {
              // If the array is for gallery_images, send it as gallery_images_urls 
              // ONLY if there isn't already a manual gallery_images_urls field being sent
              const targetKey = key.endsWith('_urls') ? key : `${key}_urls`;
              if (!formDataObj.has(targetKey)) {
                formDataObj.append(targetKey, JSON.stringify(value));
              }
            } else if (typeof value === 'object') {
              formDataObj.append(key, JSON.stringify(value));
            } else {
              // Skip if this is a manual urls field and it's empty, to not override the array we just appended
              if (!(key.endsWith('_urls') && value.trim() === '')) {
                // If it's a manual urls field but we already appended the array version, we can override or append.
                // Let's just append or set. Since FastAPI might take the last one, if it's empty we definitely skip it.
                if (key.endsWith('_urls') && formDataObj.has(key)) {
                  // If it has a value and it's not empty, we overwrite the previous one or let FastAPI handle it
                }
                formDataObj.append(key, value);
              }
            }
          }
        });

        // Add file fields (handle single, FileList, and Array)
        Object.keys(fileData).forEach(key => {
          const val = fileData[key];
          if (val instanceof File) {
            formDataObj.append(key, val);
          } else if (val instanceof FileList || Array.isArray(val)) {
            // Convert FileList or Array to a standard array and loop
            Array.from(val).forEach(file => {
              if (file instanceof File) {
                formDataObj.append(key, file);
              }
            });
          }
        });

        // Use Axios for upload with progress tracking
        response = await axios({
          method: method.toLowerCase(),
          url: url,
          data: formDataObj,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        });
      } else {
        // Use standard generic endpoints for non-file submissions
        const url = editItem
          ? `${API_URL}/api/${apiPath}/${editItem.id}`
          : `${API_URL}/api/${apiPath}`;

        response = await axios({
          method: method.toLowerCase(),
          url: url,
          data: formData,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      // Handle response
      const result = response.data;

      // Show appropriate message based on user role
      if (isCreator) {
        toast.success(editItem ? 'Updated successfully! Sent for re-approval.' : 'Created Successfully! Sent For Approval');
      } else if (isSuperAdmin || isAdmin) {
        toast.success(editItem ? 'Updated and published!' : 'Created and published successfully!');
      } else {
        toast.success(editItem ? 'Updated successfully!' : 'Created successfully!');
      }

      setDialogOpen(false);
      setFileData({});
      setEditItem(null);
      setUploadProgress(0);
      fetchItems();

    } catch (error) {
      console.error(error);
      let errorMsg = 'Operation failed';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMsg = detail.map(err => typeof err === 'object' ? (err.msg || JSON.stringify(err)) : String(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        } else {
          errorMsg = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
      // Wait a bit before clearing progress to let user see 100%
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const handleDelete = async (id) => {
    try {
      if (!id) {
        toast.error("Invalid item ID");
        return;
      }
      const token = localStorage.getItem('cish_token');
      const apiPath = getApiPath(contentType);
      await axios.delete(`${API_URL}/api/${apiPath}/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Deleted successfully!');
      setDeleteConfirm(null);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message);
    }
  };

  const filteredItems = items.filter(item => {
    if (!searchTerm) return true;
    return columns.some(col => {
      const value = item[col];
      if (typeof value === 'string') {
        return value.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return false;
    });
  });

  const renderFieldInput = (field) => {
    const value = formData[field.name] ?? '';

    switch (field.type) {
      case 'richtext':
        return (
          <RichTextEditor
            value={value}
            onChange={(val) => setFormData({ ...formData, [field.name]: val })}
            placeholder={field.placeholder}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
            className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E6D2B] focus:border-transparent resize-y"
            placeholder={field.placeholder || ''}
          />
        );

      case 'file':
        return (
          <FileUploadInput
            field={field}
            value={fileData[field.name]}
            existingUrl={formData[field.name] || formData[field.name.replace('_file', '_url')]}
            formData={formData}
            setFormData={setFormData}
            onDeleteServerFile={deleteFileFromServer}
            onChange={(file) => {
              if (file) {
                setFileData({ ...fileData, [field.name]: file });
              } else {
                // Clear both file data and form data for this field
                const newFileData = { ...fileData };
                delete newFileData[field.name];
                setFileData(newFileData);

                // Also clear the corresponding URL field in formData
                const urlFieldName = field.name.replace('_file', '_url');
                const newFormData = { ...formData };
                newFormData[field.name] = '';
                newFormData[urlFieldName] = '';
                setFormData(newFormData);
              }
            }}
          />
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
            className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E6D2B] focus:border-transparent bg-white"
          >
            <option value="">Select...</option>
            {field.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'checkbox':
        // Hide publish-related checkboxes for creators
        if (isCreator && (field.name === 'is_published' || field.name === 'visible_to_public')) {
          return (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                ⚠️ Content visibility is controlled by approval workflow. Your content will be reviewed before publishing.
              </p>
            </div>
          );
        }

        return (
          <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => setFormData({ ...formData, [field.name]: e.target.checked })}
              className="w-5 h-5 rounded border-gray-300 text-[#0E6D2B] focus:ring-[#0E6D2B]"
              disabled={isCreator && (field.name === 'is_published' || field.name === 'visible_to_public')}
            />
            <span className="text-sm text-gray-700">
              {field.checkboxLabel || 'Enable'}
            </span>
          </label>
        );

      case 'date':
        return (
          <Input
            type="date"
            value={value ? value.split('T')[0] : ''}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
            className="max-w-xs"
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={value}
            min={field.min ?? 0}
            max={field.max}
            step={field.step ?? 1}
            onChange={(e) => setFormData({ ...formData, [field.name]: parseInt(e.target.value) || 0 })}
            className="max-w-xs"
          />
        );

      default:
        return (
          <Input
            type="text"
            value={value}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
            placeholder={field.placeholder || ''}
          />
        );
    }
  };

  const renderCellValue = (item, col) => {
    const value = item[col];

    // Handle boolean values
    if (typeof value === 'boolean') {
      return value ? (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
          <Check className="w-3 h-3" /> Yes
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-500 rounded-full text-xs font-medium">
          <X className="w-3 h-3" /> No
        </span>
      );
    }

    // Handle date values
    if (col.includes('date') && value) {
      const date = new Date(value);
      return isNaN(date.getTime()) ? value : date.toLocaleDateString('en-IN');
    }

    // Handle image URLs - show thumbnail
    if ((col.includes('image') || col.includes('photo')) && value && typeof value === 'string') {
      const imgSrc = value.startsWith('http') ? value : `${API_URL}${value}`;
      return (
        <img
          src={imgSrc}
          alt=""
          className="w-10 h-10 object-cover rounded border"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    }

    // Handle priority specially
    if (col === 'priority') {
      return (
        <span className={`px-2 py-1 rounded text-xs font-medium ${value > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
          }`}>
          {value || 0}
        </span>
      );
    }

    // Handle long text
    if (typeof value === 'string' && value.length > 50) {
      // Strip HTML tags for display
      const plainText = value.replace(/<[^>]*>/g, '');
      return plainText.substring(0, 50) + '...';
    }

    return value || '-';
  };

  const groups = groupedFields();
  const hasMultipleGroups = groups.length > 1;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
          <p className="text-gray-500 mt-1">{subtitle}</p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="bg-[#0E6D2B] hover:bg-[#054018]"
          data-testid="add-new-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New
        </Button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-4 border-[#0E6D2B] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No items found. Click &ldquo;Add New&rdquo; to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                  {columns.map(col => (
                    <th key={col} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {col.replace(/_/g, ' ')}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredItems.map((item, index) => (
                  <tr key={item.id} className={`hover:bg-gray-50 ${item.is_active === false ? 'opacity-50 grayscale bg-gray-50' : ''}`}>
                    <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                    {columns.map(col => (
                      <td key={col} className="px-4 py-3 text-sm text-gray-700">
                        {renderCellValue(item, col)}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Approval Status Badge (if applicable) */}
                        <div className="mr-2 flex items-center gap-2">
                          {item.is_active === false && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold uppercase rounded border border-red-200">
                              Inactive / Deleted
                            </span>
                          )}
                          <StatusBadge status={item.workflow_status} isPublished={item.is_published} />
                        </div>

                        {(isSuperAdmin || isAdmin || (isCreator && item.created_by === user?.sub)) && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(item)}
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteConfirm(item.id)}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </>
                        )}

                        <WorkflowActions
                          item={item}
                          contentType={contentType}
                          onUpdate={fetchItems}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editItem ? `Edit ${title.slice(0, -11)}` : `Add New ${title.slice(0, -11)}`}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">

            {/* Show Progress Bar during upload */}
            {saving && uploadProgress > 0 && uploadProgress < 100 && (
              <div className="space-y-2 p-4 bg-gray-50 rounded-lg border">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Uploading files...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-2" />
                <p className="text-xs text-gray-400">Please wait, do not close this window.</p>
              </div>
            )}

            {hasMultipleGroups ? (
              <Tabs value={activeFieldGroup} onValueChange={setActiveFieldGroup} className="w-full">
                <TabsList className="mb-4 flex-wrap h-auto">
                  {groups.map(([key, group]) => (
                    <TabsTrigger key={key} value={key}>{group.label}</TabsTrigger>
                  ))}
                </TabsList>
                {groups.map(([key, group]) => (
                  <TabsContent key={key} value={key} className="space-y-4">
                    {group.fields.map(field => (
                      <div key={field.name} className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {renderFieldInput(field)}
                        {field.hint && <p className="text-xs text-gray-500">{field.hint}</p>}
                      </div>
                    ))}
                  </TabsContent>
                ))}
              </Tabs>
            ) : (
              <div className="space-y-4">
                {fields.map(field => (
                  <div key={field.name} className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {renderFieldInput(field)}
                    {field.hint && <p className="text-xs text-gray-500">{field.hint}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#0E6D2B] hover:bg-[#054018]"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  {uploadProgress > 0 && uploadProgress < 100 ? `Uploading (${uploadProgress}%)` : 'Saving...'}
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to delete this item? This action cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
